
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow
} from "@/components/ui/table";
import {
  MoreHorizontal, Eye, Edit, ShieldX, Check, Trash2,
  Lock, Unlock, AlertTriangle
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

import { AdminRoleIndicator } from './AdminRoleIndicator';
import { AdminRoleToggle } from './AdminRoleToggle';
import { CheckCircle } from 'lucide-react';

interface UserTableProps {
  users: any[];
  sortBy: string;
  sortOrder: "asc" | "desc";
  handleSort: (column: string) => void;
  confirmDeleteUser: (userId: string) => void;
  toggleVerifiedStatus: (userId: string, isCurrentlyVerified: boolean) => void;
  toggleBlockedStatus: (userId: string, isCurrentlyBlocked: boolean) => void;
  checkAdminStatus: (userId: string) => Promise<boolean>;
  toggleAdminRole: (userId: string, isCurrentlyAdmin: boolean) => void;
  openUserDetails: (user: any) => void;
  openEditUser: (user: any) => void;
  hardcodedAdminId: string;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  sortBy,
  sortOrder,
  handleSort,
  confirmDeleteUser,
  toggleVerifiedStatus,
  toggleBlockedStatus,
  checkAdminStatus,
  toggleAdminRole,
  openUserDetails,
  openEditUser,
  hardcodedAdminId
}) => {
  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead 
                className="cursor-pointer"
                onClick={() => handleSort('full_name')}
              >
                Nom {sortBy === 'full_name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead 
                className="cursor-pointer"
                onClick={() => handleSort('email')}
              >
                Email {sortBy === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead 
                className="cursor-pointer"
                onClick={() => handleSort('type')}
              >
                Type {sortBy === 'type' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead 
                className="cursor-pointer"
                onClick={() => handleSort('created_at')}
              >
                Date d'inscription {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}
              </TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">
                  Aucun utilisateur trouvé.
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} className={user.blocked ? "bg-red-50" : ""}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {user.full_name || 'Non renseigné'}
                      {user.verified && (
                        <CheckCircle className="h-4 w-4 text-blue-500" />
                      )}
                      {user.blocked && (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{user.email || 'Non renseigné'}</TableCell>
                  <TableCell>
                    <Badge variant={user.type === 'professional' ? 'blue' : 'secondary'}>
                      {user.type === 'professional' ? 'Professionnel' : 'Utilisateur'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <AdminRoleIndicator 
                      userId={user.id} 
                      checkAdminStatus={checkAdminStatus}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>
                          Actions
                        </DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => openUserDetails(user)}>
                          <Eye className="h-4 w-4 mr-2" /> Voir détails
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditUser(user)}>
                          <Edit className="h-4 w-4 mr-2" /> Modifier
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => toggleVerifiedStatus(user.id, !!user.verified)}
                        >
                          {user.verified ? (
                            <>
                              <ShieldX className="h-4 w-4 mr-2" /> Retirer vérification
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-2" /> Vérifier l'utilisateur
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => toggleBlockedStatus(user.id, !!user.blocked)}
                        >
                          {user.blocked ? (
                            <>
                              <Unlock className="h-4 w-4 mr-2" /> Débloquer le compte
                            </>
                          ) : (
                            <>
                              <Lock className="h-4 w-4 mr-2" /> Bloquer le compte
                            </>
                          )}
                        </DropdownMenuItem>
                        <AdminRoleToggle
                          userId={user.id}
                          checkAdminStatus={checkAdminStatus}
                          toggleAdminRole={toggleAdminRole}
                        />
                        {user.id !== hardcodedAdminId && (
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => confirmDeleteUser(user.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
